import React from 'react';
import {
  Activity,
  Bike,
  Clock3,
  Dumbbell,
  Flame,
  HeartPulse,
  Plus,
  Waves,
} from 'lucide-react';

const typeMeta = (type) => {
  if (type === 'running') return { icon: <Activity size={15} strokeWidth={1.8} />, tone: 'bg-lime-50 text-lime-700 ring-lime-100' };
  if (type === 'cycling') return { icon: <Bike size={15} strokeWidth={1.8} />, tone: 'bg-blue-50 text-blue-700 ring-blue-100' };
  if (type === 'gym') return { icon: <Dumbbell size={15} strokeWidth={1.8} />, tone: 'bg-lime-50 text-lime-700 ring-lime-100' };
  if (type === 'stretching') return { icon: <HeartPulse size={15} strokeWidth={1.8} />, tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100' };
  if (type === 'swimming') return { icon: <Waves size={15} strokeWidth={1.8} />, tone: 'bg-blue-50 text-blue-700 ring-blue-100' };
  return { icon: <Activity size={15} strokeWidth={1.8} />, tone: 'bg-zinc-100 text-zinc-700 ring-zinc-200' };
};

const intensityFor = (duration) => {
  const minutes = Number(duration) || 0;
  if (minutes >= 75) return 'high';
  if (minutes >= 35) return 'medium';
  return 'low';
};

const IntensityBadge = ({ duration, t }) => {
  const intensity = intensityFor(duration);
  const tone =
    intensity === 'high'
      ? 'bg-rose-50 text-rose-700 ring-rose-100'
      : intensity === 'medium'
      ? 'bg-amber-50 text-amber-700 ring-amber-100'
      : 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ring-1 ${tone}`}>
      {t(`fitness.intensities.${intensity}`)}
    </span>
  );
};

const ActivityTypeBadge = ({ type, t }) => {
  const meta = typeMeta(type);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black ring-1 ${meta.tone}`}>
      {meta.icon}
      {t(`fitness.activityTypes.${type}`, type)}
    </span>
  );
};

const EmptyState = ({ t, onAdd }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-8 text-center" data-testid="activity-empty">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
      <Activity size={26} strokeWidth={1.8} />
    </div>
    <h2 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
      {t('fitness.totalActivities')}
    </h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
      {t('fitness.emptyBody')}
    </p>
    <button
      type="button"
      onClick={onAdd}
      className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
    >
      <Plus size={16} strokeWidth={1.8} />
      {t('fitness.addActivity')}
    </button>
  </div>
);

const ActivityCard = ({ activity, t, locale }) => {
  const dateStr = new Date(activity.date).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" data-testid={`activity-row-${activity.id}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ActivityTypeBadge type={activity.activity_type} t={t} />
            <IntensityBadge duration={activity.duration} t={t} />
          </div>
          <h3 className="mt-3 text-lg font-black text-zinc-950">
            {t(`fitness.activityTypes.${activity.activity_type}`, activity.activity_type)}
          </h3>
          <p className="mt-1 text-sm text-zinc-500">{dateStr}</p>
        </div>
        <div className="rounded-lg bg-zinc-950 px-4 py-3 text-center text-lime-300">
          <p className="text-2xl font-black">{activity.duration}</p>
          <p className="text-xs font-black uppercase tracking-[0.12em]">{t('common.min')}</p>
        </div>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {activity.distance != null ? (
          <Metric icon={<Activity size={15} />} label={t('fitness.distance')} value={`${activity.distance} km`} />
        ) : null}
        {activity.calories != null ? (
          <Metric icon={<Flame size={15} />} label={t('fitness.calories')} value={`${activity.calories} kcal`} />
        ) : null}
        {activity.avg_heart_rate != null ? (
          <Metric icon={<HeartPulse size={15} />} label={t('fitness.heartRate')} value={`${activity.avg_heart_rate} ${t('fitness.heartRateUnit')}`} />
        ) : null}
      </div>
      {activity.notes ? (
        <p className="mt-4 rounded-lg bg-zinc-50 p-3 text-sm leading-6 text-zinc-600">{activity.notes}</p>
      ) : null}
    </article>
  );
};

const Metric = ({ icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm">
    <span className="text-zinc-400">{icon}</span>
    <span className="text-zinc-500">{label}</span>
    <span className="ml-auto font-black text-zinc-950">{value}</span>
  </div>
);

const ActivityTab = ({ activities, t, locale, onAdd }) => {
  const avgHRRows = activities.filter((activity) => activity.avg_heart_rate);
  const avgHR = avgHRRows.length
    ? Math.round(avgHRRows.reduce((sum, activity) => sum + Number(activity.avg_heart_rate), 0) / avgHRRows.length)
    : null;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <Summary icon={<Activity size={22} />} label={t('fitness.totalActivities')} value={activities.length} testId="fitness-stat-total" />
        <Summary
          icon={<Clock3 size={22} />}
          label={t('common.duration')}
          value={activities.reduce((sum, activity) => sum + (Number(activity.duration) || 0), 0)}
          detail={t('common.min')}
        />
        <Summary
          icon={<HeartPulse size={22} />}
          label={t('fitness.avgHeartRate')}
          value={avgHR ? `${avgHR} ${t('fitness.heartRateUnit')}` : '-'}
          testId="fitness-stat-hr"
        />
      </div>
      {activities.length === 0 ? (
        <EmptyState t={t} onAdd={onAdd} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {activities.map((activity) => (
            <ActivityCard key={activity.id} activity={activity} t={t} locale={locale} />
          ))}
        </div>
      )}
    </div>
  );
};

const Summary = ({ icon, label, value, detail, testId }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" data-testid={testId}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-zinc-500">{label}</p>
        <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">{value}</p>
        {detail ? <p className="mt-1 text-sm text-zinc-500">{detail}</p> : null}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-lime-50 text-lime-700 ring-1 ring-lime-100">
        {icon}
      </div>
    </div>
  </div>
);

export default ActivityTab;
